import { FAVOURITES_IGNORED_ERROR_CODES, removeItem } from 'apis/list';
import { logRemoveItemFromFavourites } from 'helpers/analytics';
import { publish } from 'helpers/pubsub';
import { removeItemFromStore } from 'list/helpers/store';
import { REMOVE_FAILURE, REMOVE_LOADING, REMOVE_SUCCESS, SingleItemPayload } from 'list/pubsub';
import translate from 'list/utils/translate';
import { favouritesUrl } from 'utils/settings';
import { reportError } from 'helpers/sentry';

export async function removeFromLists(
    payload: SingleItemPayload,
    listNames?: string[],
    listIds?: string[],
    listItemQuantityMap?: Record<string, number>
) {
    const skipToast = false;
    const keepIcon = false;
    publish(REMOVE_LOADING, payload);
    if (listIds) {
        try {
            for (const element of listIds) {
                const quantity = listItemQuantityMap?.[element] ?? 0;
                await removeItem(payload.productNumber, element);
                removeItemFromStore(payload.productNumber);
                logRemoveItemFromFavourites(
                    payload.source,
                    element,
                    payload.productNumber,
                    payload.productType,
                    quantity,
                    payload?.ga
                );
            }
            const separatorLabel = translate('And');
            publish(REMOVE_SUCCESS, { ...payload, keepIcon, skipToast });
            window.ikea.toast(
                translate('Item was removed from lists', {
                    productName: payload.productName,
                    listNames:
                        listNames?.join().replace(/,([^,]*)$/, ` ${separatorLabel} $1`) ??
                        translate('Default list name'),
                }),
                { label: translate('Show'), callbackOrHref: favouritesUrl },
                null
            );
            return true;
        } catch (e) {
            reportError(e as Error);
            publish(REMOVE_FAILURE, { ...payload, skipToast });
            window?.ikea?.toast(
                translate('GENERIC_ERROR'),
                { label: translate('Show'), callbackOrHref: favouritesUrl },
                null
            );
            return false;
        }
    }
    try {
        const res = await removeItem(payload.productNumber);
        const quantity = listItemQuantityMap?.[res.data.list.listId] ?? 0;
        removeItemFromStore(payload.productNumber);
        publish(REMOVE_SUCCESS, { ...payload, skipToast });
        window?.ikea?.toast(
            translate('Item was removed from lists', {
                productName: payload.productName,
                listNames: translate('Default list name'),
            }),
            { label: translate('Show'), callbackOrHref: favouritesUrl },
            null
        );
        logRemoveItemFromFavourites(
            payload.source,
            res.data.list.listId,
            payload.productNumber,
            payload.productType,
            quantity,
            payload?.ga
        );
    } catch (e) {
        const ignoreError = !!FAVOURITES_IGNORED_ERROR_CODES.find(errorCode =>
            (e as Error).message?.includes(errorCode)
        );
        // If the error is one of the whitelisted ones, do not report it and simulate that the item was removed from the list successfully.
        // We can have this behaviour because the whitelisted errors occur due to sync issues between different browser tabs.
        if (ignoreError) {
            publish(REMOVE_SUCCESS, { ...payload, skipToast });
            window?.ikea?.toast(
                translate('Item was removed from generic list', {
                    productName: payload.productName,
                }),
                { label: translate('Show'), callbackOrHref: favouritesUrl },
                null
            );
            return true;
        }
        reportError(e as Error);
        publish(REMOVE_FAILURE, { ...payload, skipToast });
        window?.ikea?.toast(
            translate('GENERIC_ERROR'),
            { label: translate('Show'), callbackOrHref: favouritesUrl },
            null
        );
        return false;
    }
}
